import React from 'react';
import { Node, Editor, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';
import { AulaPlugin } from '../plugins';
import { getCurrentNode, isCurrentSelectionCollapsed } from '../../lib/queries';
import { BlockLink } from '../../components/BlockLink/BlockLink';
import { insertVoidElement } from '../../lib/commands';
import { fetchMeta } from '../../lib/fetchMeta';
import { blockLinkType } from './blockLinkType';
import { getValidUrl } from '../../utils/getValidUrl';
import { AulaEditorContext } from '../../types';

const getFullUrl = ({ text, editor }: { text: string; editor: Editor }) =>
  isCurrentSelectionCollapsed(editor) && getValidUrl(text);

export const insertBlockLink = (
  url: string,
  editor: Editor,
  context?: Omit<AulaEditorContext, 'entities'>,
) => {
  if (context == null) {
    throw new Error('context undefined');
  }

  const entity = context.createEntity({
    type: 'meta',
    url: decodeURIComponent(url),
  });

  void fetchMeta(url, context.config.apiUrl)
    .then((data) => {
      context.updateEntity(entity, {
        data,
      });
    })
    .catch(() => {
      context.updateEntity(entity, {
        data: {
          title: url,
        },
      });
    });

  insertVoidElement(editor, {
    type: blockLinkType,
    entity,
  });
};

export const blockLink: AulaPlugin = (editableProps, editor, context) => {
  const { insertText, insertFragment, isVoid } = editor;

  editor.isVoid = (element) => {
    if (element.type === blockLinkType) return true;

    return isVoid(element);
  };

  // Handles case where url was copied from within the editor
  editor.insertFragment = (nodes: Node[]) => {
    const text = Node.string(nodes[0]);
    const fullUrl = getFullUrl({ editor, text });
    if (fullUrl) {
      insertBlockLink(fullUrl, editor, context);

      return;
    }

    insertFragment(nodes);
  };

  // Handles case where url was copied from outside of editor e.g. from the address bar
  editor.insertText = (text: string) => {
    if (text.length > 1) {
      const elements = text.split(' ');
      elements.forEach((element, index) => {
        const fullUrl = getFullUrl({ editor, text: element });
        if (fullUrl) {
          insertBlockLink(fullUrl, editor, context);
        } else {
          insertText(element);
          if (index !== elements.length - 1) insertText(' ');
        }
      });
    } else {
      insertText(text);
    }
  };

  return {
    ...editableProps,
    renderElement: (props) => {
      const { children, element, attributes } = props;

      if (element.type !== blockLinkType && editableProps.renderElement) {
        return editableProps.renderElement(props);
      }

      const isSelected = getCurrentNode(editor) === element;

      return (
        // subtracting 40px = the width of the plus btn + drag handler
        // from the total width so the block link doesn't overflow
        <div {...attributes} style={{ width: 'calc(100% - 40px)' }}>
          <BlockLink
            {...props}
            isReadOnly={ReactEditor.isReadOnly(editor)}
            isSelected={isSelected}
            onDelete={() => {
              Transforms.delete(editor, {
                at: ReactEditor.findPath(editor, element),
                voids: true,
              });
              ReactEditor.focus(editor);
            }}
            onCopy={() => {
              ReactEditor.focus(editor);

              if (!ReactEditor.isReadOnly(editor)) {
                setTimeout(() => {
                  const path = ReactEditor.findPath(editor, element);
                  Transforms.select(editor, path);
                });
              }
            }}
          />
          {children}
        </div>
      );
    },
  };
};
